import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, FileSpreadsheet, ArrowUpDown, Users } from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '@/lib/downloads';

const COLS = [
  ['position', '#'], ['name', 'Piloto'], ['dorsal', 'Dorsal'], ['verified', 'Acreditados'], ['pending', 'En revisión'],
  ['points', 'Puntos'], ['challenge', 'Reto'], ['pct', 'Reto %'],
];

export default function PilotProgressTable({ eventId, eventTitle, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: 'position', dir: 1 });
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    axios.get(`${API}/rally-master/events/${eventId}/pilot-progress`).then((r) => setData(r.data)).catch(() => toast.error('No se pudo cargar el progreso'));
  }, [open, eventId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const val = (p, k) => (k === 'challenge' ? p.selected_challenge?.name || '' : k === 'pct' ? p.selected_challenge?.pct ?? -1 : p[k] ?? '');
    const s = q.trim().toLowerCase();
    return data.pilots
      .filter((p) => !s || p.name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s) || String(p.dorsal || '').includes(s))
      .sort((a, b) => { const x = val(a, sort.key), y = val(b, sort.key); return (x > y ? 1 : x < y ? -1 : 0) * sort.dir; });
  }, [data, sort, q]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'name' || key === 'challenge' ? 1 : -1 }));

  const exportExcel = async () => {
    setExporting(true);
    try { await downloadBlob(`${API}/rally-master/events/${eventId}/pilot-progress/excel`, `progreso_pilotos_${eventTitle}.xlsx`); toast.success('Excel descargado'); }
    catch { toast.error('No se pudo exportar'); } finally { setExporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid="pilot-progress-dialog">
        <DialogHeader className="p-5 pb-3 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-violet-600" /> Progreso de pilotos · {eventTitle}</DialogTitle>
          <DialogDescription>{data ? `${data.pilots.length} pilotos · ${data.total_waypoints} waypoints. Pulsa una columna para ordenar.` : 'Cargando…'}</DialogDescription>
          <div className="flex gap-2 pt-1">
            <Input placeholder="Buscar piloto, email o dorsal…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10" data-testid="pilot-progress-search" />
            <Button type="button" variant="outline" onClick={exportExcel} disabled={exporting} className="h-10 border-green-300 text-green-700 shrink-0" data-testid="pilot-progress-excel-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Excel
            </Button>
          </div>
        </DialogHeader>
        <div className="p-3 overflow-x-auto">
          {!data && <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin text-violet-600 mx-auto" /></div>}
          {data && (
            <table className="w-full text-sm min-w-[720px]" data-testid="pilot-progress-table">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  {COLS.map(([k, label]) => (
                    <th key={k} className="py-2 px-2 cursor-pointer select-none whitespace-nowrap hover:text-gray-900" onClick={() => toggleSort(k)} data-testid={`pilot-progress-sort-${k}`}>
                      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown className={`w-3 h-3 ${sort.key === k ? 'text-violet-600' : 'text-gray-300'}`} /></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.registration_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`pilot-progress-row-${p.registration_id}`}>
                    <td className="py-2 px-2 text-gray-500">{p.position}</td>
                    <td className="py-2 px-2">
                      <p className="font-medium text-gray-900">{p.name}</p>
                      <p className="text-[11px] text-gray-500">{p.email}</p>
                      {p.completed_challenges.length > 0 && <p className="text-[11px] text-emerald-700 font-medium">✓ {p.completed_challenges.map((c) => c.name).join(', ')}</p>}
                    </td>
                    <td className="py-2 px-2">{p.dorsal ? <span className="bg-gray-900 text-white text-[11px] font-bold rounded px-1.5">#{p.dorsal}</span> : '—'}</td>
                    <td className="py-2 px-2 font-semibold text-gray-800">{p.verified}<span className="text-gray-400 font-normal">/{p.total}</span></td>
                    <td className="py-2 px-2">{p.pending ? <span className="text-amber-700 font-semibold">{p.pending}</span> : <span className="text-gray-400">0</span>}</td>
                    <td className="py-2 px-2 font-bold text-violet-800">{p.points.toLocaleString('es-ES')}</td>
                    <td className="py-2 px-2 text-gray-700">{p.selected_challenge?.name || <span className="text-gray-400">sin reto</span>}</td>
                    <td className="py-2 px-2 min-w-[120px]">
                      {p.selected_challenge && (
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${p.selected_challenge.completed ? 'bg-emerald-600' : 'bg-violet-500'}`} style={{ width: `${p.selected_challenge.pct}%` }} /></div>
                          <span className="text-xs font-semibold w-9 text-right">{p.selected_challenge.pct}%</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && rows.length === 0 && <p className="py-8 text-center text-gray-500">Sin pilotos que coincidan.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
